import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationRedemptionSchema } from 'utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormPostSeizure, FormPostSeizureProps } from './FormPostSeizure';
import { TFile, TFilePhoto, TypeLead } from 'types/user';
import { getFilesById, updatePreSeizuresFail, updateStatusTransition } from 'services/files';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { compact, pickBy } from 'lodash';

export const useInjection = ({ initDataForm, isEdit, isPre }: Partial<FormPostSeizureProps>) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const methods = useForm<FormPostSeizure>({
    resolver: yupResolver(validationRedemptionSchema),
    defaultValues: {
      quickNote: ''
    }
  });
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const documents = methods.watch('documents') ?? [];
  const fileDocumentAdd = methods.watch('fileDocumentAdd') ?? [];
  useEffect(() => {
    if (initDataForm) {
      const cleanedInitDataForm = pickBy(initDataForm, (v) => v != null);
      methods.reset(cleanedInitDataForm);
    }
  }, [initDataForm]);

  const handleDeleteDocument = (doc: Partial<TFilePhoto>) => {
    methods.setValue(
      'documents',
      documents.filter((file) => file.id !== doc.id)
    );
  };

  const handleDeleteFileAdd = (file: File) => {
    methods.setValue(
      'fileDocumentAdd',
      fileDocumentAdd.filter((f) => f.name !== file.name)
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;

    const newFiles = Array.from(fileList);
    methods.setValue('fileDocumentAdd', [...fileDocumentAdd, ...newFiles]);
  };
  const [file, setFile] = useState<TFile>();
  const handleGetFileDetail = useCallback(
    async (id: number) => {
      const { file } = (await getFilesById(Number(id))) as { file: TFile };
      setFile(file);
      methods.setValue('fileId', Number(file.id));
      methods.setValue('quickNote', file?.quickNote || '');
      methods.setValue('debtorDetail.name', file.firstName + file.lastName);
      methods.setValue('debtorDetail.vin', file.vin);
      methods.setValue('debtorDetail.fileNumber', file.fileNumber);
      methods.setValue('documents', file?.fileDocuments);
    },
    [methods, isEdit]
  );
  const { asyncCallback: asyncHandleGetFileDetail } = useAsyncCallback(handleGetFileDetail, []);
  useEffect(() => {
    if (searchParams?.get('fileId') || initDataForm?.fileId) {
      asyncHandleGetFileDetail(Number(searchParams?.get('fileId')) || initDataForm?.fileId);
    }
  }, [searchParams?.get('fileId'), initDataForm?.fileId]);

  const optionsAddress = useMemo(() => {
    return compact(
      file?.leads.map((item) => {
        if (
          item.type === TypeLead.POE ||
          item.type === TypeLead.POR ||
          item.type === TypeLead.OTHER_ADDRESS ||
          item.type === TypeLead.MVS
        ) {
          return {
            value: String(item.id),
            label: `${item.address}, ${item.city}, ${item.province}`
          };
        }
        return;
      })
    );
  }, [file]);

  const [isShowModalCompleted, setIsShowModalCompleted] = useState(false);
  const [isShowModalFail, setIsShowModalFail] = useState(false);

  const handleConfirmCompleted = async () => {
    const id = searchParams?.get('fileId') || initDataForm?.fileId;
    await updateStatusTransition(Number(id), 'CLOSE_REDEMPTION');
    setIsShowModalCompleted(false);
    navigate('/admin/redemptions');
  };

  const { asyncCallback: asyncHandleConfirmCompleted } = useAsyncCallback(
    handleConfirmCompleted,
    []
  );

  const handleConfirmFailed = async () => {
    const id = searchParams?.get('fileId') || initDataForm?.fileId;
    const status = isPre ? 'OPENED' : 'SEIZURE';
    if (isPre) {
      await updatePreSeizuresFail(Number(id));
    } else {
      await updateStatusTransition(Number(id), status);
    }
    setIsShowModalFail(false);
    navigate('/admin/redemptions');
  };
  const { asyncCallback: asyncHandleConfirmFailed } = useAsyncCallback(handleConfirmFailed, []);

  return {
    methods,
    onViewFile: () =>
      navigate(`/admin/files/${initDataForm?.fileId || searchParams?.get('fileId')}`),
    setIsShowModal,
    isShowModal,
    documents,
    fileDocumentAdd,
    handleDeleteDocument,
    handleDeleteFileAdd,
    handleFileChange,
    optionsAddress,
    isShowModalCompleted,
    setIsShowModalCompleted,
    isShowModalFail,
    setIsShowModalFail,
    handleConfirmCompleted: asyncHandleConfirmCompleted,
    handleConfirmFailed: asyncHandleConfirmFailed
  };
};
